import React from 'react';
import Draggable from 'react-draggable';

// Shared components and utilities
import { 
    OmniIconComponent, 
    OmniDropdownComponent, 
    OmniTooltipNextComponent, 
    OmniStyleComponent,
    Utils 
} from '../../../../../shared';
import { CSVUploadComponent } from './CampaignImport';
import { FiltersMenu } from './CampaignImportExportMenu';

export class FilterForAgGrid extends React.Component {
    nodeRef = null;
    defaultState = {
        filterOptions: {
            campaignName: '',
            startDate: '',
            endDate: '',
            plannedNetMediaMin: '',
            plannedNetMediaMax: '',
            orderedNetMediaMin: '',
            orderedNetMediaMax: '',
            clientCode: '',
            productCode: '',
            estimateCode: ''
        },
        isMenuActive: false,
        exportStatusOptions: [
            { "id": "Accepted", "value": "Accepted" },
            { "id": "Campaign Sent", "value": "Campaign Sent" },
            { "id": "Error", "value": "Error" },
            { "id": "Export Error", "value": "Export Error" },
            { "id": "Needs Update", "value": "Needs Update" },
            { "id": "Not Exported", "value": "Not Exported" },
            { "id": "Synced", "value": "Synced" },
            { "id": "Sync Error", "value": "Sync Error" },
            { "id": "Updated", "value": "Updated" },
        ],
        displayFilters: true,
        columns: []
    };
    constructor(props) {
        super(props);
        this.state = this.defaultState;
        this.nodeRef = React.createRef();
    }

    componentDidMount() {
        this.handleDropdownClick();
        this.loadColumns();
    }

    /**
     * Loads and processes AG Grid column definitions into a hierarchical tree structure.
     * This method retrieves column definitions from the AG Grid API and transforms them into a 
     * nested tree structure that can be used for column visibility management in the filter panel.
     * It handles both grouped columns (with children) and individual leaf columns, maintaining
     * the hierarchical relationship and visibility state of each column.
     * @description
     * The method performs the following operations:
     * 1. Retrieves column definitions from the AG Grid API
     * 2. Recursively builds a tree structure from flat column definitions
     * 3. Preserves column grouping and hierarchy
     * 4. Tracks visibility state for each column
     * 5. Updates component state with the processed column tree
     */
    loadColumns = () => {
        const { gridApi } = this.props;
        if (!gridApi) return;

        const colDefs = gridApi.getColumnDefs();

        const buildTree = (defs, parentKey = "") =>
            defs.map((def, idx) => {
                const key = `${parentKey}${def.headerName || def.field || def.colId}-${idx}`;
                if (def.children) {
                    return {
                        key,
                        group: true,
                        headerName: def.headerName,
                        children: buildTree(def.children, key + "-")
                    };
                } else {
                    return {
                        key,
                        group: false,
                        colId: def.colId,
                        headerName: def.headerName || def.field,
                        visible: !def.hide
                    };
                }
            });

        this.setState({ columns: buildTree(colDefs || []) });
    };

    /**
     * Toggles the visibility of a specific column in the AG Grid.
     * Searches for the column in both top-level and nested column definitions,
     * then toggles its visibility state and refreshes the column tree.
     * 
     * @param {string} colId - The unique column ID to toggle
     * @returns {void}
     */
    handleToggle = (colId) => {
        const { gridApi } = this.props;
        if (!gridApi) {
            console.error("gridApi is not available");
            return;
        }
        const columnDefs = gridApi.getColumnDefs() || [];
        let colDef = columnDefs.find((c) => c.colId === colId);
        if (!colDef) {
            // search for coldef in children
            const searchInChildren = (children) => {
                for (let child of children) {
                    if (child.colId === colId) return child;
                    if (child.children) {
                        const found = searchInChildren(child.children);
                        if (found) return found;
                    }
                }
                return null;
            };
            colDef = searchInChildren(columnDefs);
            if (!colDef) {
                console.warn(`Column with colId ${colId} not found in any children`);
                return;
            }
        }
        const currentlyVisible = !colDef.hide;
        gridApi.setColumnsVisible([colId], !currentlyVisible);
        this.loadColumns();
    };

    /**
     * Toggles visibility for all columns within a column group.
     * If all children are visible, hides them all; otherwise shows them all.
     * 
     * @param {Object} group - The column group object containing children
     * @returns {void}
     */
    handleGroupToggle = (group) => {
        const { gridApi } = this.props;
        if (!gridApi) return;

        const allChildren = this.getAllLeafCols(group.children);
        const allVisible = allChildren.every((c) => c.visible);

        gridApi.setColumnsVisible(
            allChildren.map((c) => c.colId),
            !allVisible
        );

        this.loadColumns();
    };

    /**
     * Recursively collects all leaf columns from a hierarchical column tree.
     * Traverses through column groups to find individual columns without children.
     * 
     * @param {Array} nodes - Array of column nodes to traverse
     * @returns {Array} Array of leaf column objects
     */
    getAllLeafCols = (nodes) => {
        let result = [];
        nodes.forEach((node) => {
            if (node.group || node.children) {
                result = result.concat(this.getAllLeafCols(node.children));
            } else {
                result.push(node);
            }
        });
        return result;
    };

    /**
     * Toggles visibility for all columns in the grid.
     * If all columns are visible, hides them all; otherwise shows them all.
     * 
     * @returns {void}
     */
    handleViewAllToggle = () => {
        const { gridApi } = this.props;
        if (!gridApi) return;

        const colDefs = gridApi.getColumnDefs() || [];
        const allLeafCols = this.getAllLeafCols(colDefs);

        const allVisible = allLeafCols.every((c) => !c.hide);
        // if all visible → hide all, else → show all
        gridApi.setColumnsVisible(
            allLeafCols.map((c) => c.colId),
            !allVisible
        );

        this.loadColumns();
    };

    /**
     * Recursively renders the hierarchical column tree with interactive checkboxes.
     * Creates column groups with indeterminate states and individual column toggles.
     * 
     * @param {Array} nodes - Array of column nodes to render
     * @param {boolean} isRoot - Whether this is the root level of the tree
     * @returns {Array} Array of JSX elements representing the column tree
     */
    renderTree = (nodes, isRoot = false) =>
        nodes.map((node, index) =>
            node.group ? (
                <div key={node.key} className="column-group">
                    <label>
                        <input
                            type="checkbox"
                            className="toggle-checkbox"
                            ref={(el) => {
                                if (el) {
                                    const allChildren = this.getAllLeafCols(node.children);
                                    const allVisible = allChildren.every((c) => c.visible);
                                    const noneVisible = allChildren.every((c) => !c.visible);

                                    el.checked = allVisible;
                                    el.indeterminate = !allVisible && !noneVisible;
                                }
                            }}
                            onChange={() => this.handleGroupToggle(node)}
                        />
                        {node.headerName}
                    </label>
                    <div className="group-children" style={{ marginLeft: "16px" }}>
                        {this.renderTree(node.children, false)}
                    </div>
                </div>
            ) : (
                <div key={node.key} className={`column-item ${index === 0 && isRoot ? 'first-column-item' : ''}`}>
                    <label>
                        <input
                            type="checkbox"
                            className="toggle-checkbox"
                            checked={node.visible}
                            onChange={() => this.handleToggle(node.colId)}
                        />
                        {node.headerName}
                    </label>
                </div>
            )
        );

    handleDropdownClick() {
        if (!this.loaded) {
            this.loaded = true;
            // Get all dropdowns on the page that aren't hoverable.
            this.dropdowns = document.querySelectorAll('.dropdown:not(.is-hoverable)');

            if (this.dropdowns.length > 0) {
                // For each dropdown, add event handler to open on click.
                this.dropdowns.forEach((el) => {
                    el.addEventListener('click', (e) => {
                        //first close all dropdown
                        this.closeDropdowns(e);
                        if (!el.classList.contains("is-active")) {
                            el.classList.toggle('is-active');
                            let container = document.querySelectorAll('.dropdown-content');
                            let exists = false;
                            container.forEach((item) => {
                                if ((!exists && item === e.target) || item.contains(e.target)) {
                                    exists = true
                                }
                            })

                            if (!exists) {
                                e.stopPropagation();
                                e.preventDefault();
                            }
                        }
                    });
                });

                // If user clicks outside dropdown, close it.
                document.addEventListener('click', (e) => {
                    this.closeDropdowns(e);
                });
            }
        }
    }

    closeDropdowns = (e) => {
        this.dropdowns.forEach(function (el) {
            el.classList.remove('is-active');
        });
    }

    handleModalClick = (event) => {
        event.stopPropagation();
    }

    /**
    * The above code is a JavaScript function called `toggleMenu` that is used to toggle the
    * visibility of a dropdown menu. When the function is called, it prevents the default behavior of
    * the event and stops it from propagating further. It then toggles the visibility of the dropdown
    * menu by adding or removing the CSS class "show" to the element with the ID
    * "dropdown-menu-content".
    * @param {html event} event
    */
    toggleMenu = (event) => {
        event.stopPropagation();
        event.preventDefault();
        document.getElementById("dropdown-menu-content").classList.toggle("show");
        const isMenuActive = document.getElementById("dropdown-menu-content")?.classList.contains("show") ? true : false;
        if (isMenuActive) {
            document.querySelectorAll("om2-dropdown-impl").forEach(eachEle => {
                eachEle.shadowRoot.querySelector(".dropdown-content").style.position = "initial";
            });
        }
        this.setState({ isMenuActive })
    }

    getEventTarget(e) {
        e = e || window.event;
        return e.target || e.srcElement;
    }

    onValueChange = (type, event) => {
        let target = this.getEventTarget(event);
        const value = target.textContent;
        this.updateFilterOptions(type, value);
    };

    /**
    *  Description The above code is defining a function called `onDataChange` that takes two parameters: `type`
    *  and `event`. Inside the function, it checks if the `type` parameter is either "startDate" or
    *  "endDate". If it is, it formats the `value` of the `event.target` using a utility function
    *  called `formatDateObject` from a module called `Utils`. Finally, it calls another function
    *  called `updateFilterOptions` with the `type` and `value` parameters.
    *  @param {string} type
    *  @param {html event} event
    */
    onDataChange = (type, event) => {
        let value = Utils.getValue(event);
        if (type === "startDate" || type === "endDate") {
            value = Utils.formatDateObject(value);
        }
        this.updateFilterOptions(type, value);
    }

    updateFilterOptions = (type, value) => {
        let filterOptions = { ...this.state.filterOptions };
        filterOptions[type] = value;
        this.setState({ filterOptions: filterOptions });
    }

    /**
    * Description The above code is defining an arrow function called `onClearFilter` that takes an event object
    * as its parameter. Inside the function, it sets the `filterOptions` state of the component to its
    * default state, calls the `onApplyFilter` function with the default filter options as its
    *  argument, and then toggles the menu. This function is likely used to reset the filter options to
    *  their default values and apply the default filter to the data being displayed. 
    *  @param {html event} event
    */
    onClearFilter = (event) => {
        this.setState({ filterOptions: this.defaultState.filterOptions });
        this.props.onApplyFilter(this.defaultState.filterOptions);
        this.toggleMenu(event);
    }

    /**
    * Description The above code is defining an arrow function called `applyFilter` that takes an event as an
    * argument. Within the function, it calls a function `onApplyFilter` that is passed as a prop to
    * the component, and passes in the current state of `filterOptions` as an argument. It then calls
    * another function `toggleMenu` and passes in the event as an argument. The purpose of this code
    * is to apply a filter based on the current state of `filterOptions` and then toggle a menu.
    * @param {html event} event
    */
    applyFilter = (event) => {
        this.props.onApplyFilter(this.state.filterOptions);
        this.toggleMenu(event);
    }


    // Method to find out passed item is null, mostly used as callback method in array methods
    isNull = (item) => !item

    changeTab = (event) => {
        const tab = event.target.innerText;
        if (tab === "Filters") {
            this.setState({ displayFilters: true });
        } else if (tab === "Columns") {
            this.setState({ displayFilters: false });
        }
    }

    handleExportMenuClose = (event) => {
        event.target.closest("omni-menu")?.close();
    };

    handleImportMenuItemClick = (event) => {
        const modalEl = document.getElementById("csv-import-modal");
        if (modalEl) {
          modalEl.show();
        }
        this.handleExportMenuClose(event);
    };

    closeCSVDialog = () => {
        const modalEl = document.getElementById("csv-import-modal");
        if (modalEl) {
          modalEl.close();
        }
    };
    
    handleExportMenuItemClick = (event) => {
        this.handleExportMenuClose(event);
    };


    render() {
        const { isMenuActive, filterOptions, columns } = this.state;
        const disableApplyFilter = (Object.values(filterOptions).every(this.isNull));  // If every value is null, disable the apply button
        const allLeafCols = this.getAllLeafCols(columns);
        const allVisible = allLeafCols.every((c) => c.visible);
        const noneVisible = allLeafCols.every((c) => !c.visible);

        return (
            <div>
                <OmniStyleComponent>
                    <div
                        className={`menu-trigger ag-grid-filter-wrapper filter-container ${
                            isMenuActive ? "bg-blue" : ""
                        }`}
                    >
                        <OmniTooltipNextComponent
                            className="aggrid-filter-menu-trigger"
                            onClick={this.toggleMenu}
                        >
                            <button slot="invoker" aria-haspopup="true" className={`icon`}>
                                <OmniIconComponent slot="end" className="menu-trigger is-size-2" icon-id="omni:interactive:filter" />
                            </button>
                            <div slot="content">Filters</div>
                            <span>All Filters</span>
                        </OmniTooltipNextComponent>
                        <FiltersMenu
                            handleExportMenuItemClick={this.handleExportMenuItemClick}
                            handleImportMenuItemClick={this.handleImportMenuItemClick}
                        />
                    </div>
                    <CSVUploadComponent
                        closeCSVDialog={this.closeCSVDialog}
                    />

                    <Draggable nodeRef={this.nodeRef} axis="both" bounds='body' defaultPosition={{ x: 54, y: 8 }}>

                        <div ref={this.nodeRef} className='aggrid-filter-panel'>
                            <div className="dropdown-menu-content campaign-filter-container aggrid-filter-container" id="dropdown-menu-content" role="menu">
                                <div>

                                    <div className="filter-header">
                                        <div>
                                            <button className={'button' + (this.state.displayFilters ? "" : " is-text")} onClick={event => this.changeTab(event)}>Filters</button>
                                            <button className={'button' + (this.state.displayFilters ? " is-text" : "")} onClick={event => this.changeTab(event)}>Columns</button>
                                        </div>
                                        <div className='filter-close-icon'>
                                            <OmniIconComponent onClick={this.toggleMenu} className="is-size-2 has-text-almost-black" icon-id="omni:interactive:close"></OmniIconComponent>
                                        </div>
                                    </div>

                                    <hr className='filter-header-bar' />
                                    {this.state.displayFilters ?

                                        <div className="filter-sections">
                                            <div className='filter-sections-list'>

                                                <section className="section-item media-direction-column clear-all-section">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <a className="media-link" onClick={this.onClearFilter}>Clear All</a>
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Campaign name</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <input type="text" placeholder="Campaign Name" value={this.state.filterOptions.campaignName}
                                                            onChange={this.onDataChange.bind(this, 'campaignName')} className="input text filter-field">
                                                        </input>
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Export Status</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <OmniDropdownComponent
                                                            typeahead
                                                            searchindropdown
                                                            options={this.state.exportStatusOptions}
                                                            value={this.state.filterOptions.status ? [this.state.filterOptions.status] : []}
                                                            onDropdownChange={(event) => this.onDataChange("status", event)}
                                                            placeholder="Select Option"
                                                            hidefooter={true}
                                                        ></OmniDropdownComponent>
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column side-section">
                                                    <label>
                                                        <div className="media-flex media-justify-space-between is-7 filter-left-box filter-label">
                                                            <span className="filter-label-text">Start date</span>
                                                        </div>
                                                        <div className="full-width">
                                                            <input type="date" data-date={this.state.filterOptions.startDate !== "" ? this.state.filterOptions.startDate : "MM DD,YYYY"} value={this.state.filterOptions.startDate}
                                                                onChange={this.onDataChange.bind(this, 'startDate')} className="input date is-large filter-field input-with-icon">
                                                            </input>
                                                        </div>
                                                    </label>
                                                    <label>
                                                        <div className="media-flex media-justify-space-between filter-right-box is-7 filter-label">
                                                            <span className="filter-label-text">End date</span>
                                                        </div>
                                                        <div className="full-width">
                                                            <input type="date" data-date={this.state.filterOptions.endDate !== "" ? this.state.filterOptions.endDate : "MM DD,YYYY"} onChange={this.onDataChange.bind(this, 'endDate')} value={this.state.filterOptions.endDate} className="input date is-large filter-field input-with-icon">
                                                            </input>
                                                        </div>
                                                    </label>
                                                </section>

                                                <section className="section-item media-direction-column side-section">
                                                    <label>
                                                        <span className="filter-label-text">Planned Net Media Min</span>
                                                        <div className="full-width">
                                                            <input type="text" placeholder="Enter Sum" value={this.state.filterOptions.plannedNetMediaMin}
                                                                onChange={this.onDataChange.bind(this, 'plannedNetMediaMin')} className="input text filter-field">
                                                            </input>
                                                        </div>
                                                    </label>
                                                    <label>
                                                        <span className="filter-label-text">Planned Net Media Max</span>
                                                        <div className="full-width">
                                                            <input type="text" placeholder="Enter Sum" value={this.state.filterOptions.plannedNetMediaMax}
                                                                onChange={this.onDataChange.bind(this, 'plannedNetMediaMax')} className="input text filter-field">
                                                            </input>
                                                        </div>
                                                    </label>
                                                </section>

                                                <section className="section-item media-direction-column side-section">
                                                    <label>
                                                        <span className="filter-label-text">Ordered Net Media Min</span>
                                                        <div className="full-width">
                                                            <input type="text" placeholder="Enter Sum" value={this.state.filterOptions.orderedNetMediaMin}
                                                                onChange={this.onDataChange.bind(this, 'orderedNetMediaMin')} className="input text filter-field">
                                                            </input>
                                                        </div>
                                                    </label>
                                                    <label>
                                                        <span className="filter-label-text">Ordered Net Media Max</span>
                                                        <div className="full-width">
                                                            <input type="text" placeholder="Enter Sum" value={this.state.filterOptions.orderedNetMediaMax}
                                                                onChange={this.onDataChange.bind(this, 'orderedNetMediaMax')} className="input text filter-field">
                                                            </input>
                                                        </div>
                                                    </label>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Client Code</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Client Code"
                                                            value={filterOptions.clientCode}
                                                            onChange={this.onDataChange.bind(this, 'clientCode')}
                                                            className="input text filter-field"
                                                        />
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Product Code</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Product Code"
                                                            value={filterOptions.productCode}
                                                            onChange={this.onDataChange.bind(this, 'productCode')}
                                                            className="input text filter-field"
                                                        />
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column">
                                                    <div className="media-flex media-justify-space-between is-7 filter-label">
                                                        <span className="filter-label-text">Estimate Code</span>
                                                    </div>
                                                    <div className="full-width">
                                                        <input
                                                            type="text"
                                                            placeholder="Enter Estimate Code"
                                                            value={filterOptions.estimateCode}
                                                            onChange={this.onDataChange.bind(this, 'estimateCode')}
                                                            className="input text filter-field"
                                                        />
                                                    </div>
                                                </section>

                                                <section className="section-item media-direction-column last-section-item">
                                                    <div className="action-btns">
                                                        <button className={`${disableApplyFilter ? 'secondary' : ''}`} disabled={disableApplyFilter} onClick={this.applyFilter}>Apply filters</button>
                                                    </div>
                                                </section>
                                            </div>
                                        </div>
                                        :

                                        <div className="filter-sections">

                                            <div className="view-all">
                                                <label>
                                                    <input type="checkbox" className="toggle-checkbox" onChange={this.handleViewAllToggle}
                                                        ref={(el) => {
                                                            if (el) {
                                                                el.checked = allVisible;
                                                                el.indeterminate = !allVisible && !noneVisible;
                                                            }
                                                        }} />
                                                    View All Columns
                                                </label>
                                            </div>

                                            <hr className="columns-separator" />
                                            {this.renderTree(columns, true)}
                                        </div>
                                    }
                                </div>
                            </div>
                        </div>
                    </Draggable>
                </OmniStyleComponent>
            </div>
        )
    }
}
